import React, { FormEvent } from "react";
import { Package, Building2, FileText, AlertCircle, Image, Plus, Info } from "lucide-react";
import { matchCase } from "src/utils";
import { CheckInUnknownArrivalMapper } from "src/mappers";
import { ValidationErrors } from "src/errors";
import {
  CheckInUnknownArrivalFormData,
  CheckInUnknownArrivalFormDataKey,
  NonOrderProductCheckInInputField
} from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { NonOrderProductCheckInInputTable } from "src/components/tables/inputTables";
import { MultiFileUpload, TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";

interface CheckInUnknownArrivalFormProps {
  formData: CheckInUnknownArrivalFormData;
  vendorsOptions: string[];
  validationErrors?: ValidationErrors<CheckInUnknownArrivalFormDataKey>;
  formLvlErrorMessage: string;
  handleInputChange: (
    field: CheckInUnknownArrivalFormDataKey,
    value: CheckInUnknownArrivalFormData[keyof CheckInUnknownArrivalFormData]
  ) => void;
  handleSubmit: (e: FormEvent) => Promise<void>;
}

const CheckInUnknownArrivalForm: React.FC<CheckInUnknownArrivalFormProps> = ({
  formData,
  vendorsOptions,
  validationErrors,
  formLvlErrorMessage,
  handleInputChange,
  handleSubmit
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSetProductCheckIns: React.Dispatch<React.SetStateAction<NonOrderProductCheckInInputField[]>> = (
    updatedProductCheckIns
  ) => {
    handleInputChange("nonOrderProductCheckIns", updatedProductCheckIns as NonOrderProductCheckInInputField[]);
  };

  const handleAddProductCheckIn = () => {
    handleInputChange("nonOrderProductCheckIns", [
      ...formData.nonOrderProductCheckIns,
      CheckInUnknownArrivalMapper.initNonOrderProductCheckInInputField()
    ]);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Check-in Unknown Arrival</h1>
            <p className="text-gray-300 mt-2 text-lg">Record arrival details for unidentified shipments</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Vendor Information Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  Vendor Information
                </h3>
                <TextInputBox
                  id="vendor"
                  placeholder="Vendor"
                  onChange={(value) => handleInputChange("vendorName", value)}
                  value={formData.vendorName}
                  onBlur={() => handleInputChange("vendorName", matchCase(formData.vendorName, vendorsOptions))}
                  dropDownOptions={[...vendorsOptions, "Unknown"]}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("vendorName")} />
              </div>

              {/* ASN Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  ASN
                </h3>
                <TextInputBox
                  id="asn"
                  placeholder="ASN"
                  onChange={(value) => handleInputChange("asn", value)}
                  value={formData.asn}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("asn")} />
                <div className="flex items-center space-x-2 mt-2 text-sm text-gray-600">
                  <Info className="w-4 h-4 text-primary-500" />
                  <span>Arrivals should specify the Vendor and ASN. If missing, enter 'Unknown'</span>
                </div>
              </div>

              {/* Reason Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Reason for Unknown Arrival
                </h3>
                <TextInputBoxLarge
                  id="reason-for-unknown-arrival"
                  placeholder="Reason (e.g. why was this arrival unidentifiable?)"
                  onChange={(value) => handleInputChange("reasonForUnknownArrival", value)}
                  value={formData.reasonForUnknownArrival}
                  maxLength={500}
                />
                <ValidationErrorMessage
                  message={validationErrors?.getErrorMessageByFormDataKey("reasonForUnknownArrival")}
                />
              </div>

              {/* Products Section */}
              {formData.nonOrderProductCheckIns.length > 0 && (
                <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                  <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Products
                  </h3>
                  <NonOrderProductCheckInInputTable
                    nonOrderProductCheckIns={formData.nonOrderProductCheckIns}
                    setNonOrderProductCheckIns={handleSetProductCheckIns}
                    minNonOrderProductCheckIns={1}
                  />
                  <ValidationErrorMessage
                    message={validationErrors?.getErrorMessageByFormDataKey("nonOrderProductCheckIns")}
                  />
                </div>
              )}

              {/* Add Product Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleAddProductCheckIn}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  type="button"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Product</span>
                </button>
              </div>

              {/* Additional Details Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Additional Details
                </h3>
                <TextInputBoxLarge
                  id="additional-details"
                  placeholder="Additional Details – e.g., size metrics, defects, etc. (Optional)"
                  onChange={(value) => handleInputChange("additionalDetails", value)}
                  value={formData.additionalDetails}
                  maxLength={1000}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("additionalDetails")} />
              </div>

              {/* Images Upload Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Image className="w-4 h-4 mr-2" />
                  Check-in Images
                </h3>
                <MultiFileUpload
                  uploadText="Upload Images (Optional)"
                  uploadedFiles={formData.checkInImages}
                  onFileChange={(files) => handleInputChange("checkInImages", files ?? [])}
                  fileFormat="image/jpeg, image/png, image/gif, image/webp"
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("checkInImages")} />
              </div>

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!formData.vendorName}
                  type="submit"
                >
                  Check-in
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { CheckInUnknownArrivalForm };
